import { Amplify } from 'aws-amplify'
import { events } from 'aws-amplify/data'

const environment = (process.env.REACT_APP_ENV).toLowerCase()

const isSupportedEnvironment = ['development', 'production'].includes(environment)
const hasEventConfig = Boolean(process.env.REACT_APP_EVENT_API_URL && process.env.REACT_APP_EVENT_API_KEY)
const isAppSyncEnabled = isSupportedEnvironment && hasEventConfig

const createSubscriptionHandle = (unsubscribe) => {
  const handle = () => unsubscribe()
  handle.unsubscribe = unsubscribe

  return handle
}

if (isAppSyncEnabled) {
  Amplify.configure({
    API: {
      Events: {
        endpoint: process.env.REACT_APP_EVENT_API_URL,
        region: 'us-east-2',
        defaultAuthMode: 'apiKey',
        apiKey: process.env.REACT_APP_EVENT_API_KEY
      }
    }
  })
}

class AppSyncEventManager {
  constructor() {
    this.channels = new Map()
  }

  subscribe(channelName, callback) {
    if (!isAppSyncEnabled) {
      return createSubscriptionHandle(() => {})
    }

    let channelData = this.channels.get(channelName)

    if (!channelData) {
      channelData = {
        channel: null,
        subscription: null,
        listeners: new Set(),
        connectingPromise: null,
      }

      channelData.connectingPromise = events.connect(channelName)
        .then((channel) => {
          const current = this.channels.get(channelName)

          // This channel was already removed while connection was pending.
          if (!current) {
            channel?.close?.()
            return
          }

          current.channel = channel
          current.subscription = channel.subscribe({
            next: (data) => {
              current.listeners.forEach((listener) => {
                listener(data.event, channelName)
              })
            },
            error: (error) => {
              // eslint-disable-next-line no-console
              console.error(`AppSync error [${channelName}]`, error)
            },
          })

          // If everyone unsubscribed before connect completed, clean up immediately.
          if (current.listeners.size === 0) {
            current.subscription?.unsubscribe()
            current.channel?.close?.()
            this.channels.delete(channelName)
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(`AppSync connect error [${channelName}]`, error)
          this.channels.delete(channelName)
        })

      this.channels.set(channelName, channelData)
    }

    channelData.listeners.add(callback)

    const unsubscribe = () => this.unsubscribe(channelName, callback)

    return createSubscriptionHandle(unsubscribe)
  }

  unsubscribe(channelName, callback) {
    if (!isAppSyncEnabled) {
      return
    }
    const channelData = this.channels.get(channelName)

    if (!channelData) return

    // Remove only this component listener
    channelData.listeners.delete(callback)

    // Keep connection if other components still use it
    if (channelData.listeners.size > 0) return

    // Connection is still pending; delete now and let connect cleanup when it resolves.
    if (!channelData.subscription) {
      this.channels.delete(channelName)
      return
    }

    // Last listener removed → cleanup
    channelData.subscription?.unsubscribe()
    channelData.channel?.close?.()

    this.channels.delete(channelName)
  }

  unsubscribeAll() {
    this.channels.forEach((channelData) => {
      channelData.subscription?.unsubscribe()
      channelData.channel?.close()
    })

    this.channels.clear()
  }
}

export default new AppSyncEventManager()